import React, { useEffect, useState} from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import MapView from '../components/map/MapView';
import Loading from '../components/common/Loading';
import { CheckCircle, XCircle, HelpCircle, Layers } from 'lucide-react';

/* Filtres de statut */
const FILTERS = [
  { key: 'all',      label: 'Tous',       icon: Layers,      color: null          },
  { key: 'open',     label: 'Ouverts',    icon: CheckCircle, color: '#00c9a7'     },
  { key: 'closed',   label: 'Fermes',     icon: XCircle,     color: '#f0564a'     },
  { key: 'uncertain',label: 'Incertains', icon: HelpCircle,  color: '#f5a623'     },
];

const MapPage = () => {
  const [allShops, setAllShops] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('all');

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'shops'), snap => {
      setAllShops(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, err => { console.error(err); setLoading(false); });
    return () => unsub();
  }, []);

  const filtered = filter === 'all' ? allShops : allShops.filter(s => s.status === filter);

  const openCount      = allShops.filter(s => s.status === 'open').length;
  const closedCount    = allShops.filter(s => s.status === 'closed').length;
  const uncertainCount = allShops.filter(s => s.status === 'uncertain').length;

  if (loading) return <Loading message="Chargement de la carte…"/>;

  return (
    <div className="page-enter" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100dvh - var(--header-h) - var(--nav-h) - env(safe-area-inset-bottom, 0px))' }}>

      {/* ── Barre superieure : titre + compteurs ── */}
      <div style={{
        padding: '0.75rem 1rem 0',
        background: 'var(--bg)',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem', gap: '1rem' }}>
          <div style={{ flexShrink: 0 }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.3px', whiteSpace: 'nowrap' }}>
              Carte des commerces
            </h2>
            <p style={{ fontSize: '0.72rem', color: 'var(--text-3)', marginTop: '0.1rem' }}>
              {allShops.length} etablissement{allShops.length > 1 ? 's' : ''} references
            </p>
          </div>

          {/* Compteurs compacts */}
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <StatPill count={openCount}      color="#00c9a7" label=""/>
            <StatPill count={closedCount}    color="#f0564a" label=""/>
            {uncertainCount > 0 && <StatPill count={uncertainCount} color="#f5a623" label=""/>}
          </div>
        </div>

        {/* Filtres */}
        <div style={{ display: 'flex', gap: '0.4rem', overflowX: 'auto', paddingBottom: '0.7rem', scrollbarWidth: 'none' }}>
          {FILTERS.map(f => {
            const Icon     = f.icon;
            const isActive = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                aria-pressed={isActive}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                  padding: '0.42rem 0.9rem',
                  borderRadius: 'var(--r-full)', border: 'none',
                  background: isActive
                    ? (f.color
                        ? `linear-gradient(145deg, ${f.color}, ${f.color}cc)`
                        : 'linear-gradient(145deg,#00d9b4,#009e85)')
                    : 'var(--bg)',
                  color: isActive ? '#fff' : 'var(--text-2)',
                  fontWeight: 700, fontSize: '0.8rem', fontFamily: 'inherit',
                  cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                  boxShadow: isActive
                    ? `3px 3px 10px ${f.color ? f.color + '55' : 'rgba(0,158,133,.40)'},-2px -2px 6px rgba(0,0,0,.10)`
                    : 'var(--neu-sm)',
                  transition: 'all 0.18s',
                }}
              >
                <Icon size={13} strokeWidth={2.5}/>
                {f.label}
                {f.key !== 'all' && (
                  <span style={{
                    background: isActive ? 'rgba(255,255,255,.25)' : 'var(--bg)',
                    boxShadow: isActive ? 'none' : 'var(--neu-in-sm)',
                    borderRadius: '999px', padding: '0.06rem 0.45rem',
                    fontSize: '0.66rem', fontWeight: 800,
                    color: isActive ? '#fff' : 'var(--text-2)',
                    minWidth: 20, textAlign: 'center',
                  }}>
                    {allShops.filter(s => s.status === f.key).length}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Carte ── */}
      <div style={{
        flex: 1,
        margin: '0 0.75rem 0.75rem',
        borderRadius: 'var(--r-xl)',
        overflow: 'hidden',
        boxShadow: 'var(--neu)',
        minHeight: 0,
        position: 'relative',
      }}>
        <MapView shops={filtered}/>

        {/* Legende flottante */}
        <div style={{
          position: 'absolute', bottom: 60, left: 12, zIndex: 1000,
          background: 'var(--bg)',
          boxShadow: 'var(--neu-sm)',
          borderRadius: 'var(--r-lg)',
          padding: '0.6rem 0.8rem',
          display: 'flex', flexDirection: 'column', gap: '0.35rem',
          minWidth: 100,
        }}>
          <div style={{ fontSize: '0.6rem', fontWeight: 800, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '0.1rem' }}>
            Legende
          </div>
          {[
            { color: '#00c9a7', label: 'Ouvert'    },
            { color: '#f0564a', label: 'Ferme'     },
            { color: '#f5a623', label: 'Incertain' },
          ].map(({ color, label }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
              <span style={{
                width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                background: color,
                boxShadow: `0 0 5px ${color}80`,
              }}/>
              <span style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-2)' }}>{label}</span>
            </div>
          ))}
        </div>

        {/* Message aucun resultat */}
        {filtered.length === 0 && (
          <div style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            background: 'var(--bg)',
            boxShadow: 'var(--neu)',
            borderRadius: 'var(--r-xl)',
            padding: '1rem 1.5rem',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.2rem' }}>
              Aucun magasin
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-2)' }}>
              pour le filtre selectionne
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Pilule compteur ────────────────────────────────────── */
const StatPill = ({ count, color, label }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '0.3rem',
    padding: '0.3rem 0.6rem',
    borderRadius: 'var(--r-full)',
    background: 'var(--bg)',
    boxShadow: 'var(--neu-sm)',
  }}>
    <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }}/>
    <span style={{ fontSize: '0.7rem', fontWeight: 800, color: 'var(--text-2)' }}>{count}</span>
    <span style={{ fontSize: '0.65rem', color: 'var(--text-3)', fontWeight: 600 }}>{label}</span>
  </div>
);

export default MapPage;